export interface ReadingStreakDay {
  day: string;
  totalMs: number;
}

export interface ReadingStreakSummary {
  longestDays: number;
  longestStart: string | null;
  longestEnd: string | null;
  streakDays: Set<string>;
  currentDays: number;
  currentStreakDays: Set<string>;
}

const DAY_MS = 24 * 60 * 60 * 1000;

function dayOrdinal(day: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(day);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const date = Number(match[3]);
  const timestamp = Date.UTC(year, month, date);
  const parsed = new Date(timestamp);
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month || parsed.getUTCDate() !== date) return null;
  return Math.floor(timestamp / DAY_MS);
}

export function summarizeReadingStreaks(days: ReadingStreakDay[], asOfDay: string): ReadingStreakSummary {
  const activeByOrdinal = new Map<number, string>();
  for (const entry of days) {
    if (entry.totalMs <= 0) continue;
    const ordinal = dayOrdinal(entry.day);
    if (ordinal !== null) activeByOrdinal.set(ordinal, entry.day);
  }

  const activeDays = [...activeByOrdinal.entries()].sort((left, right) => left[0] - right[0]);
  const streakDays = new Set<string>();
  let longestDays = 0;
  let longestStart: string | null = null;
  let longestEnd: string | null = null;
  let run: Array<[number, string]> = [];
  const runs: Array<Array<[number, string]>> = [];

  const finishRun = () => {
    if (run.length === 0) return;
    runs.push(run);
    if (run.length >= 2) {
      for (const [, day] of run) streakDays.add(day);
    }
    if (run.length > longestDays) {
      longestDays = run.length;
      longestStart = run[0][1];
      longestEnd = run[run.length - 1][1];
    }
    run = [];
  };

  for (const activeDay of activeDays) {
    const previous = run.at(-1);
    if (previous && activeDay[0] !== previous[0] + 1) finishRun();
    run.push(activeDay);
  }
  finishRun();

  const currentStreakDays = new Set<string>();
  let currentDays = 0;
  const asOfOrdinal = dayOrdinal(asOfDay);
  const latestRun = runs.at(-1);
  const latestRunEnd = latestRun?.at(-1)?.[0] ?? null;
  if (
    latestRun
    && asOfOrdinal !== null
    && latestRunEnd !== null
    && latestRunEnd <= asOfOrdinal
    && latestRunEnd >= asOfOrdinal - 1
  ) {
    currentDays = latestRun.length;
    if (latestRun.length >= 2) {
      for (const [, day] of latestRun) currentStreakDays.add(day);
    }
  }

  return { longestDays, longestStart, longestEnd, streakDays, currentDays, currentStreakDays };
}
